/**
 * Creature AI — User Heuristics + Minimax Fallback
 *
 * Shares the same 29-priority heuristic system as Gaia (DionsAI),
 * but uses Minimax with alpha-beta pruning as the fallback engine
 * instead of Focused MCTS.
 *
 * The heuristics prune bad moves first, then minimax searches the
 * smaller remaining space — potentially going deeper within the
 * same time budget.
 */

import { GameState, PieceType } from "../../types";
import { IAIPlayer, AIMoveResult, AIStats, AIPlayerType } from "../Core/IAIPlayer";
import { ZodiacEngine } from "../../zodiacEngine";
import { CreatureController } from "./CreatureController";

export class CreatureAI implements IAIPlayer {
  readonly name = AIPlayerType.CREATURE;
  readonly displayName = "Creature";
  readonly description = "29-priority heuristics with minimax tactical fallback";

  private creatureController: CreatureController;
  private gameEngine: ZodiacEngine;
  private stats: AIStats = {
    movesCalculated: 0,
    averageThinkingTime: 0,
    nodesEvaluated: 0,
  };
  private totalThinkingTime = 0;

  constructor(gameEngine: ZodiacEngine) {
    this.gameEngine = gameEngine;
    this.creatureController = new CreatureController(gameEngine, false);
  }

  /**
   * Select king — random (same as Gaia for now)
   */
  selectKing(availableKings: PieceType[], isLightSide: boolean): PieceType {
    const randomIndex = Math.floor(Math.random() * availableKings.length);
    return availableKings[randomIndex];
  }

  async selectMove(gameState: GameState, maxThinkingTime: number): Promise<AIMoveResult> {
    const startTime = Date.now();

    const result = this.creatureController.findBestMove(maxThinkingTime);

    if (!result) {
      throw new Error("Creature AI failed to find a valid move");
    }

    const thinkingTime = Date.now() - startTime;

    // Update stats
    this.stats.movesCalculated++;
    this.totalThinkingTime += thinkingTime;
    this.stats.averageThinkingTime = this.totalThinkingTime / this.stats.movesCalculated;

    // Get analysis data for confidence
    const analysisData = this.creatureController.getLatestAnalysisDataDirectly();
    const confidence = this.calculateConfidence(analysisData, result.decisionType);

    // Include piece info for display
    const fromSquare = gameState.board[result.fromPosition.row]?.[result.fromPosition.col];
    const movingPiece = fromSquare?.Pieces.find(p => p.id === result.move.pieceId);

    return {
      fromPosition: result.fromPosition,
      move: result.move,
      confidence,
      thinkingTime,
      evaluation: result.minimaxData?.bestScore,
      reasoning: result.reasoning,
      principalVariation: result.minimaxData?.principalVariation,
      movingPiece: movingPiece ? { type: movingPiece.type, isKing: movingPiece.isKing } : undefined,
      decisionLog: analysisData?.preprocessingLog,
    };
  }

  getStats(): AIStats {
    return { ...this.stats };
  }

  reset(): void {
    this.stats = {
      movesCalculated: 0,
      averageThinkingTime: 0,
      nodesEvaluated: 0,
    };
    this.totalThinkingTime = 0;
  }

  configure(config: Record<string, any>): void {
    if (config.debugMode !== undefined) {
      this.creatureController.setDebugMode(config.debugMode);
    }
  }

  private calculateConfidence(analysisData: any, decisionType: string): number {
    if (!analysisData) return 0.5;

    // Priority-based decisions get high confidence
    if (decisionType === "priority") {
      const reasoning = analysisData.chosenMove || "";
      const priorityMatch = reasoning.match(/Priority (\d+)/);
      if (priorityMatch) {
        const priority = parseInt(priorityMatch[1]);
        if (priority <= 15) return 0.95;
        if (priority <= 20) return 0.8;
        return 0.65;
      }
      return 0.85;
    }

    // Minimax decisions — confidence based on score clarity
    if (analysisData.minimaxData) {
      const score = Math.abs(analysisData.minimaxData.bestScore);
      if (score > 500) return 0.9; // Clearly winning/losing
      if (score > 200) return 0.8;
      if (score > 50) return 0.7;
      return 0.6;
    }

    return 0.5;
  }
}
